/** Shared widgets used across the Document Settings tabs. */

import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Trash2, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const ACCEPT_IMAGES = 'image/png,image/jpeg,image/jpg,image/webp';
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB — must match backend

export function NumberPreview({ kind, company, marker, seq }: { kind: string; company: string; marker: string; seq: number }) {
  const year = new Date().getFullYear();
  const fallback = kind === 'Quotation' ? 'QUO'
    : kind === 'Agreement' ? 'AGR'
      : kind === 'Payslip' ? 'PAY'
        : 'INV';
  const safeMarker = (marker || '').trim() || fallback;
  const c = (company || '').trim();
  const prefix = c ? `${c}-${safeMarker}` : safeMarker;
  const number = `${prefix}-${year}-${String(Math.max(1, Math.floor(seq || 1))).padStart(6, '0')}`;
  return (
    <div className="rounded-md bg-muted/40 border px-3 py-2 text-xs">
      <span className="text-muted-foreground">Next {kind} #:</span>
      <span className="ml-2 font-mono font-medium text-foreground">{number}</span>
    </div>
  );
}

interface AssetUploaderProps {
  label: string;
  icon: React.ReactNode;
  url: string | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onSelect: (file: File | null) => void;
  onRemove?: () => void;
  busy?: boolean;
}

export function AssetUploader({ label, icon, url, inputRef, onSelect, onRemove, busy }: AssetUploaderProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="rounded-md border border-dashed bg-muted/30 h-32 flex items-center justify-center overflow-hidden">
        {url
          ? <img src={url} alt={label} className="max-h-full max-w-full object-contain p-2" />
          : <div className="flex flex-col items-center text-muted-foreground gap-1">{icon}<span className="text-xs">No {label.toLowerCase()} uploaded</span></div>}
      </div>
      <div className="flex gap-2">
        <input ref={inputRef} type="file" accept={ACCEPT_IMAGES} className="hidden" onChange={(e) => { onSelect(e.target.files?.[0] ?? null); e.target.value = ''; }} />
        <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => inputRef.current?.click()} disabled={busy}>
          <Upload className="mr-2 h-3.5 w-3.5" />{busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
        </Button>
        {onRemove && <Button type="button" variant="ghost" size="sm" onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    </div>
  );
}

interface ColorPickerFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
}

export function ColorPickerField<T extends FieldValues>({ form, name, label }: ColorPickerFieldProps<T>) {
  const value = form.watch(name) as string;
  const error = form.formState.errors[name];
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => form.setValue(name, e.target.value as never, { shouldDirty: true, shouldValidate: true })}
          className="h-9 w-12 rounded border bg-transparent cursor-pointer"
        />
        <Input value={value} onChange={(e) => form.setValue(name, e.target.value as never, { shouldDirty: true, shouldValidate: true })} placeholder="#010b17" />
      </div>
      {error && <p className="text-xs text-destructive">{error.message as string}</p>}
    </div>
  );
}
